import React from 'react';

import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import EventHandlerFacilitator from '@/components/EventHandlerFacilitator';
import renderImage from '@/modules/content/utils/renderImage';
import { EventStatusEnum, EventUnion } from '@/types/schema';
import { grayScale } from '@/utils/styles';

import useEventAction from '../../hooks/useEventAction';
import useEventMutations from '../../hooks/useEventMutations';
import ActionDialogs from './ActionDialogs';
import { eventActionComponentsRegistry } from './consts';
import ListItem from './ListItem';
import { imageStyle } from './styles';

export const getEventImage = (image: string | null | undefined, status: EventStatusEnum) => {
  return (
    <Box
      className={cn(
        'h-48 w-64 flex-shrink-0 rounded-4 bg-icon-placeholder bg-cover',
        image && imageStyle(image),
        {
          grayScale: [
            EventStatusEnum.Completed,
            EventStatusEnum.Revoked,
            EventStatusEnum.Archived,
          ].includes(status),
        },
      )}
    />
  );
};

export const renderEventImage = (event: EventUnion, width = 64, height = 48) => {
  const { cover, status } = event;

  return renderImage({
    image: cover?.url,
    width,
    height,
    contentName: event.name,
    className: cn(
      `w-[${width}] h-[${height}] object-cover`,
      [EventStatusEnum.Completed, EventStatusEnum.Revoked, EventStatusEnum.Archived].includes(
        status,
      )
        ? grayScale
        : '',
    ),
  });
};

function ListItemContainer({
  event,
  eventMutations,
}: {
  event: EventUnion;
  eventMutations: ReturnType<typeof useEventMutations>;
}) {
  const {
    state: { inViewActionDialog },
    methods,
    data,
    eventHandler,
  } = useEventAction({
    eventId: event.id,
    latestUpcomingEventId: event?.latestUpcomingEventId,
    eventMutations,
  });

  const { onAction, onDialogAction } = methods;
  const { url } = data;
  const Image = renderEventImage(event);

  return (
    <>
      <ListItem Image={Image!} event={event} url={url} onAction={onAction} />
      <React.Suspense fallback={null}>
        <ActionDialogs
          Image={Image!}
          event={event}
          inViewActionDialog={inViewActionDialog}
          onAction={onDialogAction}
        />
        <EventHandlerFacilitator
          componentsRegistry={eventActionComponentsRegistry}
          componentsState={eventHandler.componentsState}
          onCloseComponent={eventHandler.onCloseComponent}
        />
      </React.Suspense>
    </>
  );
}

export default React.memo(ListItemContainer);
